// Pure helpers for the interactive knowledge-graph exploration.

export function buildAdjacency(edges) {
  const adj = new Map();
  const add = (a, b) => {
    if (!adj.has(a)) adj.set(a, new Set());
    adj.get(a).add(b);
  };
  edges.forEach((e) => {
    add(e.source, e.target);
    add(e.target, e.source);
  });
  return adj;
}

export function neighborsOf(adj, id) {
  return adj.has(id) ? Array.from(adj.get(id)) : [];
}

// Visible = root + neighbors of the root + (for each expanded node) that node + its neighbors.
export function computeVisibleIds(edges, rootId, expandedIds) {
  const visible = new Set();
  if (!rootId) return visible;
  const adj = buildAdjacency(edges);
  const expanded = new Set(expandedIds);
  expanded.add(rootId);
  visible.add(rootId);
  expanded.forEach((id) => {
    visible.add(id);
    neighborsOf(adj, id).forEach((n) => visible.add(n));
  });
  return visible;
}

// Concentric radial layout by BFS depth from the root within the visible subgraph.
export function radialLayout(visibleIds, edges, rootId) {
  const positions = {};
  if (!rootId || visibleIds.size === 0) return positions;

  const visible = visibleIds;
  const adj = new Map();
  const link = (a, b) => {
    if (!adj.has(a)) adj.set(a, new Set());
    adj.get(a).add(b);
  };
  edges.forEach((e) => {
    if (visible.has(e.source) && visible.has(e.target)) {
      link(e.source, e.target);
      link(e.target, e.source);
    }
  });

  const depth = new Map([[rootId, 0]]);
  const queue = [rootId];
  while (queue.length) {
    const cur = queue.shift();
    const d = depth.get(cur);
    (adj.get(cur) ? Array.from(adj.get(cur)) : []).forEach((n) => {
      if (!depth.has(n)) {
        depth.set(n, d + 1);
        queue.push(n);
      }
    });
  }

  // Anything visible but disconnected in the subgraph -> outermost ring.
  let maxDepth = 0;
  depth.forEach((d) => (maxDepth = Math.max(maxDepth, d)));
  visible.forEach((id) => {
    if (!depth.has(id)) depth.set(id, maxDepth + 1);
  });

  const rings = {};
  depth.forEach((d, id) => {
    if (!rings[d]) rings[d] = [];
    rings[d].push(id);
  });

  Object.keys(rings).forEach((dStr) => {
    const d = Number(dStr);
    const ids = rings[d].sort();
    if (d === 0) {
      positions[ids[0]] = { x: 0, y: 0 };
      return;
    }
    const n = ids.length;
    const radius = Math.max(d * 300, n * 55);
    ids.forEach((id, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      positions[id] = {
        x: Math.round(radius * Math.cos(angle)),
        y: Math.round(radius * Math.sin(angle)),
      };
    });
  });

  return positions;
}

// Overview layout: group the whole graph into clusters by entity type so the map reads as
// spatial "regions" (all Concepts together, all Schools together, ...) instead of one tangle.
const TYPE_ORDER = ["knowledge_system", "school", "concept", "framework", "text", "person"];

export function clusterLayout(visibleIds, nodeMap) {
  const positions = {};
  const groups = new Map();
  visibleIds.forEach((id) => {
    const t = nodeMap[id]?.type || "other";
    if (!groups.has(t)) groups.set(t, []);
    groups.get(t).push(id);
  });
  const types = [
    ...TYPE_ORDER.filter((t) => groups.has(t)),
    ...[...groups.keys()].filter((t) => !TYPE_ORDER.includes(t)),
  ];

  const COL_W = 210, ROW_H = 100, CLUSTER_GAP = 260;
  let clusterX = 0;
  types.forEach((t) => {
    const ids = groups.get(t).slice()
      .sort((a, b) => (nodeMap[a]?.label || "").localeCompare(nodeMap[b]?.label || ""));
    const cols = Math.min(5, Math.max(1, Math.ceil(Math.sqrt(ids.length))));
    const rows = Math.ceil(ids.length / cols);
    ids.forEach((id, i) => {
      const c = i % cols;
      const r = Math.floor(i / cols);
      positions[id] = {
        x: Math.round(clusterX + c * COL_W),
        y: Math.round(r * ROW_H - (rows * ROW_H) / 2),
      };
    });
    clusterX += cols * COL_W + CLUSTER_GAP;
  });
  return positions;
}